import { useEffect, useState, useRef } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Database, DownloadSimple, ArrowClockwise, Trash, Plus, Upload, ClockCounterClockwise, WarningCircle, ShieldCheck, Warning } from '@phosphor-icons/react';

const formatSize = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

export default function Backups() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(null); // backup obj to restore
  const [uploadOpen, setUploadOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetPhrase, setResetPhrase] = useState('');
  const [resetResult, setResetResult] = useState(null);
  const fileRef = useRef();

  const load = () => {
    setLoading(true);
    api.get('/backups').then(({ data }) => setItems(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const createNow = async () => {
    setCreating(true);
    try {
      const { data } = await api.post('/backups/create');
      toast.success(`تم إنشاء نسخة (${formatSize(data.size)})`);
      load();
    } catch (err) {
      toast.error('فشل الإنشاء: ' + (err.response?.data?.detail || err.message));
    } finally { setCreating(false); }
  };

  const download = (b) => {
    const token = localStorage.getItem('token');
    window.open(`${api.defaults.baseURL}/backups/${b.id}/download?auth=${encodeURIComponent(token)}`, '_blank');
  };

  const restore = async () => {
    if (!confirmOpen) return;
    setRestoring(true);
    try {
      const { data } = await api.post(`/backups/${confirmOpen.id}/restore`);
      toast.success(`تمت الاستعادة (${data.total} سجل)`);
      setConfirmOpen(null);
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      toast.error('فشل الاستعادة: ' + (err.response?.data?.detail || err.message));
    } finally { setRestoring(false); }
  };

  const uploadRestore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm('تحذير: سيتم استبدال جميع البيانات الحالية. هل أنت متأكد؟')) return;
    setRestoring(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post('/backups/upload-restore', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`تمت الاستعادة من ${data.from} (${data.total} سجل)`);
      setUploadOpen(false);
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      toast.error('فشل: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRestoring(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const del = async (id) => {
    if (!window.confirm('حذف هذه النسخة الاحتياطية؟')) return;
    try {
      await api.delete(`/backups/${id}`);
      toast.success('تم الحذف');
      load();
    } catch { toast.error('فشل الحذف'); }
  };

  const doFactoryReset = async () => {
    setResetting(true);
    try {
      const { data } = await api.post('/admin/factory-reset-invoices', { confirm: 'RESET_INVOICES' });
      setResetResult(data);
      toast.success('تمت إعادة تهيئة الفواتير بنجاح');
    } catch (err) {
      toast.error('فشلت العملية: ' + (err.response?.data?.detail || err.message));
    } finally { setResetting(false); }
  };

  const autoCount = items.filter(i => i.trigger === 'auto').length;
  const manualCount = items.filter(i => i.trigger === 'manual').length;

  return (
    <div className="space-y-6" data-testid="backups-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#B4935A] mb-1">System Backups</div>
          <h1 className="text-3xl font-bold text-slate-900">النسخ الاحتياطية</h1>
          <p className="text-sm text-slate-500 mt-1">نسخة يومية تلقائية + نسخ يدوية عند الطلب — محفوظة على تخزين آمن</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setUploadOpen(true)} size="sm" data-testid="upload-restore-button">
            <Upload className="ms-1" size={16} /> استعادة من ملف
          </Button>
          <Button onClick={createNow} disabled={creating} className="bg-slate-900 text-white" size="sm" data-testid="create-backup-button">
            <Plus className="ms-1" size={16} /> {creating ? 'جاري الإنشاء...' : 'إنشاء نسخة الآن'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="إجمالي النسخ" value={items.length} icon={Database} />
        <Stat label="تلقائية" value={autoCount} icon={ClockCounterClockwise} tone="text-blue-600" />
        <Stat label="يدوية" value={manualCount} icon={ShieldCheck} tone="text-[#B4935A]" />
        <Stat label="الحجم الإجمالي" value={formatSize(items.reduce((s, i) => s + (i.size || 0), 0))} icon={Database} />
      </div>

      <Card className="p-4 border border-slate-200 rounded-md shadow-none bg-blue-50/50">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-blue-100 flex items-center justify-center shrink-0">
            <ClockCounterClockwise size={18} className="text-blue-700" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-900">جدولة تلقائية مُفعّلة</div>
            <div className="text-xs text-slate-600 mt-1">
              يُنشئ النظام نسخة احتياطية تلقائية كل 24 ساعة، ويحتفظ بآخر 30 نسخة تلقائية. النسخ اليدوية لا تُحذف تلقائياً.
            </div>
          </div>
        </div>
      </Card>

      {/* Danger Zone — Admin-only factory reset */}
      <Card className="border border-red-300 rounded-md shadow-none bg-red-50/30" data-testid="danger-zone">
        <div className="p-4 border-b border-red-200 flex items-center gap-2">
          <Warning weight="fill" className="text-red-600" size={18} />
          <div className="font-bold text-red-800 text-sm">منطقة الخطر — لمدير النظام فقط</div>
        </div>
        <div className="p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="max-w-2xl">
            <div className="text-sm font-semibold text-slate-900 mb-1">إعادة تهيئة بيانات الفواتير</div>
            <div className="text-xs text-slate-600 leading-loose">
              يحذف جميع الفواتير، السدادات، إجراءات المتابعة، الملاحظات، المهام، الخطابات، المرفقات، التصنيفات، حالات المتابعة، سجل الاستيراد، والمطالبات القانونية المُحالة —
              ليُصبح قسم الفواتير جاهزاً لاستيراد ملف Yardi جديد كأول استخدام.
              <br />
              <span className="text-red-700 font-semibold">لا يؤثر على:</span> حسابات المستخدمين، الصلاحيات، إعدادات النظام، لوحة التحكم، التقارير، قوالب الخطابات، سجل التدقيق، النسخ الاحتياطية.
            </div>
          </div>
          <Button
            onClick={() => { setResetOpen(true); setResetPhrase(''); setResetResult(null); }}
            className="bg-red-600 hover:bg-red-700 text-white"
            size="sm"
            data-testid="factory-reset-btn"
          >
            <Trash className="ms-1" size={14} /> إعادة تهيئة الفواتير
          </Button>
        </div>
      </Card>

      <Dialog open={resetOpen} onOpenChange={(v) => { setResetOpen(v); if (!v) setResetResult(null); }}>
        <DialogContent dir="rtl" className="max-w-lg" data-testid="factory-reset-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Warning size={22} weight="fill" /> تحذير — إعادة تهيئة نهائية
            </DialogTitle>
            <DialogDescription className="pt-2 leading-loose text-slate-700">
              سيتم حذف <b>جميع الفواتير</b> وجميع بيانات المتابعة المرتبطة بها بشكل نهائي، ولا يمكن التراجع عن هذه العملية.
            </DialogDescription>
          </DialogHeader>
          {!resetResult ? (
            <>
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs space-y-1 leading-loose text-slate-700">
                سيُحذف: الفواتير، السدادات، إجراءات المتابعة، الملاحظات، المهام، الخطابات، الإشعارات، المرفقات، التصنيفات، حالات المتابعة، سجل الاستيراد، المطالبات القانونية.<br />
                لن يُحذف: المستخدمين، الصلاحيات، إعدادات النظام، قوالب الخطابات، سجل التدقيق، النسخ الاحتياطية.
              </div>
              <div className="text-xs text-slate-600 mt-2 mb-1">للتأكيد اكتب: <span className="mono font-bold text-red-700">أوافق</span></div>
              <input
                value={resetPhrase}
                onChange={(e) => setResetPhrase(e.target.value)}
                className="w-full h-9 border border-slate-300 rounded px-3 text-sm mono"
                placeholder="أوافق"
                data-testid="factory-reset-confirm-input"
              />
              <div className="flex justify-end gap-2 mt-3">
                <Button variant="outline" onClick={() => setResetOpen(false)} disabled={resetting}>إلغاء</Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={resetting || resetPhrase.trim() !== 'أوافق'}
                  onClick={doFactoryReset}
                  data-testid="factory-reset-confirm-btn"
                >
                  <Trash className="ms-1" size={14} /> {resetting ? 'جارٍ التنفيذ...' : 'تنفيذ إعادة التهيئة'}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-md text-sm space-y-2">
                <div className="flex items-center gap-2 text-emerald-700 font-semibold">
                  <ShieldCheck size={18} weight="fill" /> تمت إعادة التهيئة بنجاح
                </div>
                <div className="text-xs text-slate-700 leading-loose">{resetResult.message}</div>
                <div className="text-xs text-slate-600">
                  إجمالي السجلات المحذوفة: <b className="tabular-nums">{resetResult.total_wiped?.toLocaleString()}</b>
                </div>
                <details className="text-xs text-slate-500 mt-1">
                  <summary className="cursor-pointer">تفاصيل الحذف حسب الجدول</summary>
                  <div className="mt-2 space-y-0.5 mono">
                    {Object.entries(resetResult.wiped_counts || {}).map(([k, v]) => (
                      <div key={k} className="flex justify-between"><span>{k}</span><span className="tabular-nums">{v}</span></div>
                    ))}
                  </div>
                </details>
              </div>
              <div className="flex justify-end mt-3">
                <Button variant="outline" onClick={() => setResetOpen(false)}>إغلاق</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Card className="border border-slate-200 rounded-md shadow-none overflow-hidden">
        <table className="w-full text-sm data-table">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-start p-3 text-xs uppercase tracking-wider text-slate-600 font-medium">الملف</th>
              <th className="text-start p-3 text-xs uppercase tracking-wider text-slate-600 font-medium">النوع</th>
              <th className="text-start p-3 text-xs uppercase tracking-wider text-slate-600 font-medium">التاريخ</th>
              <th className="text-start p-3 text-xs uppercase tracking-wider text-slate-600 font-medium">أنشأها</th>
              <th className="text-end p-3 text-xs uppercase tracking-wider text-slate-600 font-medium">السجلات</th>
              <th className="text-end p-3 text-xs uppercase tracking-wider text-slate-600 font-medium">الحجم</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-6 text-center text-slate-500">جاري التحميل...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-slate-500">لا توجد نسخ احتياطية بعد</td></tr>
            ) : items.map(b => (
              <tr key={b.id} className="border-t border-slate-100" data-testid={`backup-${b.id}`}>
                <td className="p-3 mono text-xs">{b.filename}</td>
                <td className="p-3">
                  <Badge variant="outline" className={b.trigger === 'auto' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-[#B4935A]/10 text-[#B4935A] border-[#B4935A]/30'}>
                    {b.trigger === 'auto' ? 'تلقائية' : 'يدوية'}
                  </Badge>
                </td>
                <td className="p-3 mono text-xs text-slate-600">{new Date(b.created_at).toLocaleString('ar-SA')}</td>
                <td className="p-3">{b.created_by_name}</td>
                <td className="p-3 text-end tabular-nums">{b.total_docs?.toLocaleString() || 0}</td>
                <td className="p-3 text-end tabular-nums text-slate-600">{formatSize(b.size)}</td>
                <td className="p-3 text-end">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" onClick={() => download(b)} title="تنزيل" data-testid={`download-${b.id}`}>
                      <DownloadSimple size={16} />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => setConfirmOpen(b)} title="استعادة" data-testid={`restore-${b.id}`}>
                      <ArrowClockwise size={16} className="text-blue-600" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => del(b.id)} title="حذف">
                      <Trash size={16} className="text-red-600" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Confirm restore */}
      <Dialog open={!!confirmOpen} onOpenChange={(v) => !v && setConfirmOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <WarningCircle size={22} /> تحذير: استعادة نسخة احتياطية
            </DialogTitle>
            <DialogDescription>
              سيتم استبدال جميع البيانات الحالية (الفواتير، المستأجرين، العقود، الملاحظات، الخ) بالبيانات المخزنة في هذه النسخة. هذه العملية غير قابلة للتراجع.
            </DialogDescription>
          </DialogHeader>
          {confirmOpen && (
            <div className="border border-slate-200 rounded-md p-3 bg-slate-50 space-y-1 text-sm">
              <div><span className="text-slate-500">الملف:</span> <span className="mono">{confirmOpen.filename}</span></div>
              <div><span className="text-slate-500">التاريخ:</span> {new Date(confirmOpen.created_at).toLocaleString('ar-SA')}</div>
              <div><span className="text-slate-500">السجلات:</span> {confirmOpen.total_docs}</div>
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setConfirmOpen(null)}>إلغاء</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700" onClick={restore} disabled={restoring} data-testid="confirm-restore-button">
              {restoring ? 'جاري الاستعادة...' : 'تأكيد الاستعادة'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload restore */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>استعادة من ملف نسخة احتياطية</DialogTitle>
            <DialogDescription>ارفع ملف JSON من نسخة احتياطية سابقة تم تنزيلها.</DialogDescription>
          </DialogHeader>
          <label className="border-2 border-dashed border-slate-300 rounded-md p-8 flex flex-col items-center justify-center cursor-pointer hover:border-[#B4935A]">
            <Upload size={36} className="text-slate-400 mb-3" />
            <div className="text-sm font-medium">اختر ملف JSON</div>
            <div className="text-xs text-slate-500 mt-1">سيتم استبدال البيانات الحالية</div>
            <input ref={fileRef} type="file" accept=".json,application/json" onChange={uploadRestore} className="hidden" disabled={restoring} data-testid="restore-file-input" />
          </label>
          {restoring && <div className="text-sm text-slate-600 text-center">جاري الاستعادة...</div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ label, value, icon: Icon, tone = 'text-slate-900' }) => (
  <Card className="p-4 border border-slate-200 rounded-md shadow-none">
    <div className="flex items-start justify-between mb-2">
      <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="w-8 h-8 rounded-md bg-slate-50 flex items-center justify-center">
        <Icon size={16} className="text-slate-600" />
      </div>
    </div>
    <div className={`text-2xl font-bold tabular-nums ${tone}`}>{value}</div>
  </Card>
);
